import React, { useState, useEffect, useMemo } from "react";
import { Error } from "../components/Error.component";
import { useGetDevicesQuery } from "../api/api";
import Spinner from "../components/Spinner";
import { Card, Space, Table, Row, Col, Select } from "antd";
import SearchBox from "../components/SearchBox";
import { Link } from "react-router-dom";
import '../MuseDashboard.css';

const { Option } = Select;

export const Devices = () => {
  const { data, error, isLoading } = useGetDevicesQuery();

  const [searchQuery, setSearchQuery] = useState("");
  const [devices, setDevices] = useState([]);
  const [modelFilter, setModelFilter] = useState("");
  const [osFilter, setOsFilter] = useState("");
  const [complianceFilter, setComplianceFilter] = useState("");
  const [manufacturerFilter, setManufacturerFilter] = useState("");

  useEffect(() => {
    if (data) {
      console.log(data);
      setDevices(data.data);
    }
  }, [data]);

  const filterData = useMemo(() => {
    if (!data || !data.data) return [];
    const query = searchQuery.toLowerCase();
    return data.data.filter((item) => {
      const matchesSearchQuery = Object.values(item).some((value) =>
        String(value).toLowerCase().includes(query)
      );
      const matchesModel = modelFilter ? item.model === modelFilter : true;
      const matchesOs = osFilter ? item.operatingsystem === osFilter : true;
      const matchesCompliance = complianceFilter ? item.compliancestate === complianceFilter : true;
      const matchesManufacturer = manufacturerFilter ? item.manufacturer === manufacturerFilter : true;
      return matchesSearchQuery && matchesModel && matchesOs && matchesCompliance && matchesManufacturer;
    });
  }, [data, searchQuery, modelFilter, osFilter, complianceFilter, manufacturerFilter]);
  

  const osCounts = useMemo(() => {
    if (!data || !data.data) return { macOS: 0, Windows: 0, iOS: 0, Linux: 0, Android: 0 };
    return data.data.reduce((acc, item) => {
      if (item.operatingsystem === "macOS") acc.macOS += 1;
      if (item.operatingsystem === "Windows") acc.Windows += 1;
      if (item.operatingsystem === "iOS") acc.iOS += 1;
      if (item.operatingsystem === "Linux (ubuntu)") acc.Linux += 1;
      if (item.operatingsystem === "Android") acc.Android += 1;
      return acc;
    }, { macOS: 0, Windows: 0, iOS: 0, Linux: 0, Android: 0 });
  }, [data]);
  
  const manufacturerCounts = useMemo(() => {
    if (!data || !data.data) return { Apple: 0, LENOVO: 0, HP: 0, Logitech: 0, Samsung: 0, "Dell Inc.": 0, OnePlus: 0, "Microsoft Corporation": 0 };
    return data.data.reduce((acc, item) => {
      if (item.manufacturer === "Apple") acc.Apple += 1;
      if (item.manufacturer === "LENOVO") acc.LENOVO += 1;
      if (item.manufacturer === "HP") acc.HP += 1;
      if (item.manufacturer === "Logitech") acc.Logitech += 1;
      if (item.manufacturer === "Samsung") acc.Samsung += 1;
      if (item.manufacturer === "Dell Inc.") acc["Dell Inc."] += 1;
      if (item.manufacturer === "OnePlus") acc.OnePlus += 1;
      if (item.manufacturer === "Microsoft Corporation") acc["Microsoft Corporation"] += 1;
      return acc;
    }, { Apple: 0, LENOVO: 0, HP: 0, Logitech: 0, Samsung: 0, "Dell Inc.": 0, OnePlus: 0, "Microsoft Corporation": 0 });
  }, [data]);
  
  const columns = [
    { 
      title: "Device Name", 
      dataIndex: "devicename", 
      key: "deviceName", 
      className: "table-cell-padding",
      sorter: (a, b) => a.deviceName.localeCompare(b.deviceName) 
    },
    { 
      title: "Model", 
      dataIndex: "model", 
      key: "model", 
      className: "table-cell-padding",
      sorter: (a, b) => a.model.localeCompare(b.model) 
    },
    { 
      title: "Operating System", 
      dataIndex: "operatingsystem", 
      key: "operatingsystem", 
      className: "table-cell-padding",
      sorter: (a, b) => a.operatingsystem.localeCompare(b.operatingsystem) 
    },
    { 
      title: "Manufacturer", 
      dataIndex: "manufacturer", 
      key: "manufacturer", 
      className: "table-cell-padding",
      sorter: (a, b) => a.manufacturer.localeCompare(b.manufacturer) 
    },
    { 
      title: "User", 
      dataIndex: "userdisplayname", 
      key: "userDisplayName", 
      className: "table-cell-padding",
      sorter: (a, b) => a.userDisplayName.localeCompare(b.userDisplayName) 
    },

 
    { 
      title: "Actions", 
      key: "actions", 
      render: (text, record) => <Link to={`/devices/${record.id}`}>Details</Link>, 
      className: "table-cell-padding" 
    },
  ];

  return (
    <div className="montserrat-font">
      {isLoading && <Spinner />}
      {error && <Error error={"Oops!"} />}
      {data && !error && (
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
  <Row gutter={16}>
    <Col span={12}>
      <Card title="Operating Systems" className="muse-card equal-height-card os-card">
        <Row gutter={[16, 16]}>
          <Col span={8}><h3><strong>Total devices: {devices.length}</strong></h3></Col>
          <Col span={8}><h3><strong>macOS: {osCounts.macOS}</strong></h3></Col>
          <Col span={8}><h3><strong>Windows: {osCounts.Windows}</strong></h3></Col>
          <Col span={8}><h3><strong>iOS: {osCounts.iOS}</strong></h3></Col>
          <Col span={8}><h3><strong>Linux: {osCounts.Linux}</strong></h3></Col>
          <Col span={8}><h3><strong>Android: {osCounts.Android}</strong></h3></Col>
        </Row>
      </Card>
    </Col>
    <Col span={12}>
      <Card title="Device Manufacturer" className="muse-card equal-height-card os-card">
        <Row gutter={[16, 16]}>
          <Col span={8}><h3><p><strong>Apple: {manufacturerCounts.Apple}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>HP: {manufacturerCounts.HP}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>LENOVO: {manufacturerCounts.LENOVO}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>Logitech: {manufacturerCounts.Logitech}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>Samsung: {manufacturerCounts.Samsung}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>Dell Inc.: {manufacturerCounts["Dell Inc."]}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>OnePlus: {manufacturerCounts.OnePlus}</strong></p></h3></Col>
          <Col span={8}><h3><p><strong>Microsoft Corporation: {manufacturerCounts["Microsoft Corporation"]}</strong></p></h3></Col>
        </Row>
      </Card>
    </Col>
  </Row>
          <Card className="muse-card">
            <Space direction="horizontal" size="middle" style={{ marginBottom: 16 }}>
              <SearchBox onSearch={setSearchQuery} />
              <Select
                placeholder="Filter by Model"
                onChange={value => setModelFilter(value)}
                allowClear
                style={{ width: 200 }}
              >
                {Array.from(new Set(devices.map(device => device.model))).map(model => (
                  <Option key={model} value={model}>{model}</Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by OS"
                onChange={value => setOsFilter(value)}
                allowClear
                style={{ width: 200 }}
              >
                {Array.from(new Set(devices.map(device => device.operatingsystem))).map(os => (
                  <Option key={os} value={os}>{os}</Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Compliance State"
                onChange={value => setComplianceFilter(value)}
                allowClear
                style={{ width: 200 }}
              >
                {Array.from(new Set(devices.map(device => device.compliancestate))).map(state => (
                  <Option key={state} value={state}>{state}</Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Manufacturer"
                onChange={value => setManufacturerFilter(value)}
                allowClear
                style={{ width: 200 }}
              >
                {Array.from(new Set(devices.map(device => device.manufacturer))).map(manufacturer => (
                  <Option key={manufacturer} value={manufacturer}>{manufacturer}</Option>
                ))}
              </Select>
            </Space>
            <Table
              columns={columns}
              dataSource={filterData}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                defaultPageSize: 10,
              }}
              rowKey="id"
            />
          </Card>
        </Space>
      )}
    </div>
  );
};
